/**
 * TTCPReveal - Theatrical TTCP Number Reveal
 * 
 * The anchor moment. Scale up with bounce, count up the number.
 * This is the first thing they see - it sets the anchor.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface TTCPRevealProps {
  value: number;
  isVisible: boolean;
  delay?: number;
  onComplete?: () => void;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function TTCPReveal({ 
  value, 
  isVisible, 
  delay = 0,
  onComplete 
}: TTCPRevealProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  
  // Count-up animation
  useEffect(() => {
    if (!isVisible || hasStarted) return;
    
    const startDelay = setTimeout(() => {
      setHasStarted(true);
      
      const duration = 1200; // ms
      const startTime = Date.now();
      const startValue = 0;
      const endValue = value;
      
      const easeOutExpo = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutExpo(progress);
        
        const current = Math.round(startValue + (endValue - startValue) * easedProgress);
        setDisplayValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          onComplete?.();
        }
      };
      
      requestAnimationFrame(animate);
    }, delay);
    
    return () => clearTimeout(startDelay);
  }, [isVisible, value, delay, hasStarted, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isVisible ? { 
        opacity: 1, 
        scale: 1,
      } : { opacity: 0, scale: 0.8 }}
      transition={{
        delay: delay / 1000,
        duration: 0.5,
        ease: [0.34, 1.56, 0.64, 1], // Bounce easing
      }}
      className="relative"
    >
      <span className="text-4xl font-black text-charcoal-900 tracking-tight tabular-nums">
        {formatCurrency(displayValue)}
      </span>

      {/* Subtle glow on reveal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: [0, 0.5, 0] } : { opacity: 0 }}
        transition={{
          delay: delay / 1000,
          duration: 1,
          ease: 'easeOut',
        }}
        className="absolute inset-0 blur-xl bg-charcoal-300 -z-10"
        style={{ transform: 'scale(1.5)' }}
      />
    </motion.div>
  );
}

export default TTCPReveal;
